import type {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import React, { useCallback, useEffect, useRef } from 'react'
import { cancellingPills, insertingUOMViaKeyboardPills, pillTextCache } from '../uomPillState'
import { useRegisterUOMHandlerForPillTextFocus } from './useRegisterUOMHandlerForPillTextFocus'

interface UseUOMPillBehaviorOptions {
    contentItem: Partial<TextContentElement>
    children: React.ReactNode
    onCancelNewPill?: () => void
    /** Called when UOM children drop to zero after having been present (exit inline editing). */
    onUOMChildrenGone: () => void
}

export const useUOMPillBehavior = ({
    contentItem,
    children,
    onCancelNewPill,
    onUOMChildrenGone,
}: UseUOMPillBehaviorOptions) => {
    const pillRef = useRef<HTMLElement>(null)
    const hadUomChildrenRef = useRef(false)
    const prevUomChildrenCountRef = useRef(0)
    const prevElementIdsRef = useRef<Set<string>>(new Set())
    const textInputRefs = useRef<Map<string, HTMLInputElement>>(new Map())
    // Always-fresh refs so the childrenCount effect can read current values without a dep.
    const contentItemElementsRef = useRef(contentItem.elements)
    contentItemElementsRef.current = contentItem.elements
    const pillIdRef = useRef(contentItem.id)
    pillIdRef.current = contentItem.id

    // Stable ref so the effect below can call the latest onUOMChildrenGone without
    // adding it to the dep array (avoids spurious re-runs when the callback identity changes).
    const onUOMChildrenGoneRef = useRef(onUOMChildrenGone)
    onUOMChildrenGoneRef.current = onUOMChildrenGone

    useEffect(() => {
        const id = contentItem.id
        return () => {
            if (id) {
                pillTextCache.delete(id)
                // Final backstop so the guard never outlives its pill. Safe here (unlike in
                // useUOMSave, which remounts every rebuild and would wipe the guard mid-insert).
                insertingUOMViaKeyboardPills.delete(id)
            }
        }
    }, [contentItem.id])

    const handleUOMPillClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            // Skip focus routing when an input inside the pill already has focus —
            // prevents blurring the active input when the user clicks pill padding.
            if ((e.currentTarget as HTMLElement).contains(document.activeElement)) return
            const els = (contentItem.elements ?? []) as Partial<
                ChildTextContentElement
            >[]
            const textEls = els.filter((el) => el.type === 'Text')
            if (textEls.length === 0) return

            const uomSpans = Array.from(
                (e.currentTarget as HTMLElement).querySelectorAll(
                    '[data-suggestion^="units-of-measure-"]'
                )
            )
            let targetIdx = textEls.length - 1

            for (let i = 0; i < uomSpans.length; i++) {
                if (e.clientX < uomSpans[i].getBoundingClientRect().left) {
                    targetIdx = i
                    break
                }
            }

            const targetId = textEls[targetIdx]?.id
            if (targetId) textInputRefs.current.get(targetId)?.focus()
        },
        [contentItem.elements]
    )

    const handleTextInputFocus = useRegisterUOMHandlerForPillTextFocus(
        contentItem.id
    )

    // When the UOM editor unmounts (children go from some to zero), exit inline
    // editing so the pill text span renders instead of the blank text input.
    // When UOM children increase (each new insert), focus the Text element immediately
    // before the newly inserted UOM (textBefore at the split cursor position).
    const childrenCount = React.Children.count(children)
    useEffect(() => {
        if (childrenCount === 0 && hadUomChildrenRef.current) {
            onUOMChildrenGoneRef.current()
        }
        if (childrenCount > prevUomChildrenCountRef.current) {
            const currentEls = (contentItemElementsRef.current ??
                []) as Partial<ChildTextContentElement>[]
            const prevIds = prevElementIdsRef.current
            // Find the newly appeared UOM (id absent in the previous render's elements).
            const newUOMIndex = currentEls.findIndex(
                (el: Partial<ChildTextContentElement>) =>
                    el.type === 'UnitsOfMeasure' &&
                    el.changeType === 'ADDED' &&
                    !!el.id &&
                    !prevIds.has(el.id)
            )
            const textBeforeId: string | undefined =
                newUOMIndex > 0 &&
                currentEls[newUOMIndex - 1]?.type === 'Text'
                    ? currentEls[newUOMIndex - 1].id
                    : undefined
            requestAnimationFrame(() => {
                const target = textBeforeId
                    ? textInputRefs.current.get(textBeforeId)
                    : Array.from(textInputRefs.current.values())[0]
                target?.focus()
                // Releases the Ctrl+U guard event-driven (not on a timer) once the insert commits.
                const pillId = pillIdRef.current
                if (pillId) insertingUOMViaKeyboardPills.delete(pillId)
            })
        }
        hadUomChildrenRef.current = childrenCount > 0
        prevUomChildrenCountRef.current = childrenCount
        prevElementIdsRef.current = new Set(
            ((contentItemElementsRef.current ?? []) as Partial<ChildTextContentElement>[])
                .map((el) => el.id)
                .filter((id): id is string => !!id)
        )
    }, [childrenCount])

    const handlePillTextChange = useCallback(
        (elId: string, value: string) => {
            if (!contentItem.id) return
            let pillCache = pillTextCache.get(contentItem.id)
            if (!pillCache) {
                pillCache = new Map()
                pillTextCache.set(contentItem.id, pillCache)
            }
            pillCache.set(elId, value)
        },
        [contentItem.id]
    )

    const handlePillEscape = useCallback(() => {
        if (contentItem.id) cancellingPills.add(contentItem.id)
        setReactiveWorkspaceActions({
            isInsertUOM: false,
            omWorkspaceInsertUOMHandler: undefined,
        })
        onCancelNewPill?.()
    }, [contentItem.id, onCancelNewPill])

    const hasUOMChildren = childrenCount > 0

    return {
        pillRef,
        textInputRefs,
        childrenCount,
        hasUOMChildren,
        handleUOMPillClick,
        handleTextInputFocus,
        handlePillTextChange,
        handlePillEscape,
    }
}
